import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Spinner } from "@/components/common";
import { ArrowLeft } from "lucide-react";
import { ImageUpload } from "@/components/ImageUpload";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function EditProfile() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const welcome = params.get("welcome");
  const [industries, setIndustries] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/meta").then(({ data }) => setIndustries(data.industries)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user && typeof user === "object") {
      setForm({
        name: user.name || "", mobile: user.mobile || "", city: user.city || "", state: user.state || "",
        designation: user.designation || "", industry: user.industry || "", business_name: user.business_name || "",
        intro: user.intro || "", website: user.website || "", photo_url: user.photo_url || "",
        expertise: (user.expertise || []).join(", "), connection_pref: user.connection_pref || "connections",
      });
    }
  }, [user]);

  if (!form) return <div className="container-nx section-nx"><Spinner /></div>;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        expertise: form.expertise.split(",").map((s) => s.trim()).filter(Boolean),
      };
      await api.put("/profile", payload);
      await refreshUser();
      toast.success("Profile updated!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container-nx section-nx max-w-2xl">
      <Link to="/dashboard" className="mb-6 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#0B192C]">
        <ArrowLeft className="h-4 w-4" /> Back to dashboard
      </Link>

      {welcome && (
        <div className="mb-6 rounded-xl border border-[#D4AF37]/40 bg-[#D4AF37]/10 p-4 text-sm text-[#8a6d12]" data-testid="welcome-banner">
          🎉 Welcome! Complete your profile so other members can find and connect with you.
        </div>
      )}

      <h1 className="heading-2">Edit your profile</h1>

      <form onSubmit={save} className="mt-8 space-y-5" data-testid="profile-form">
        <div className="card-nx space-y-5 p-6">
          <Field label="Full name"><input value={form.name} onChange={set("name")} className="input-nx" data-testid="profile-name-input" /></Field>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <Field label="Mobile number (private)"><input value={form.mobile} onChange={set("mobile")} className="input-nx" data-testid="profile-mobile-input" /></Field>
            <Field label="Professional designation / role"><input value={form.designation} onChange={set("designation")} className="input-nx" placeholder="e.g. Founder & CEO" data-testid="profile-designation-input" /></Field>
          </div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <Field label="City"><input value={form.city} onChange={set("city")} className="input-nx" data-testid="profile-city-input" /></Field>
            <Field label="State"><input value={form.state} onChange={set("state")} className="input-nx" data-testid="profile-state-input" /></Field>
          </div>
          <Field label="Industry">
            <Select value={form.industry} onValueChange={(v) => setForm((f) => ({ ...f, industry: v }))}>
              <SelectTrigger className="input-nx h-auto" data-testid="profile-industry-select"><SelectValue placeholder="Select industry" /></SelectTrigger>
              <SelectContent>{industries.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Business name (optional)"><input value={form.business_name} onChange={set("business_name")} className="input-nx" data-testid="profile-business-name-input" /></Field>
          <Field label="Areas of expertise (comma separated)"><input value={form.expertise} onChange={set("expertise")} className="input-nx" placeholder="e.g. CNC Machining, Export, Tooling" data-testid="profile-expertise-input" /></Field>
          <Field label="Short introduction">
            <textarea value={form.intro} onChange={set("intro")} className="input-nx min-h-[100px]" placeholder="Tell the community about yourself and your work..." data-testid="profile-intro-input" />
          </Field>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <Field label="Website (optional)"><input value={form.website} onChange={set("website")} className="input-nx" placeholder="https://" data-testid="profile-website-input" /></Field>
            <ImageUpload label="Profile photo (optional)" value={form.photo_url} onChange={(url) => setForm((f) => ({ ...f, photo_url: url }))} rounded testId="profile-photo" />
          </div>
          <Field label="Contact / connection preference">
            <Select value={form.connection_pref} onValueChange={(v) => setForm((f) => ({ ...f, connection_pref: v }))}>
              <SelectTrigger className="input-nx h-auto" data-testid="profile-pref-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="connections">Accept connection requests</SelectItem>
                <SelectItem value="introductions">Introductions only</SelectItem>
                <SelectItem value="none">Not accepting requests</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </div>
        <p className="text-xs text-slate-400">Your mobile number is kept private and never shown publicly.</p>
        <button type="submit" disabled={saving} className="btn-gold w-full sm:w-auto" data-testid="profile-save-btn">
          {saving ? "Saving..." : "Save profile"}
        </button>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-[#0B192C]">{label}</label>
      {children}
    </div>
  );
}
